/**
 * WebRTCベースの音声録音ユーティリティ（iOS対応）
 */

export interface WebRTCAudioRecorder {
  stream: MediaStream | null;
  audioChunks: Blob[];
  mimeType: string;
  isRecording: boolean;
  audioContext: AudioContext | null;
  mediaStreamSource: MediaStreamAudioSourceNode | null;
  processor: ScriptProcessorNode | null;
  recordedBuffers: Float32Array[];
  sampleRate: number;
  
  initialize: (preferredMimeTypes: string[], deviceId?: string) => Promise<void>;
  start: () => void;
  stop: () => Promise<Blob>;
  cleanup: () => void;
  
  // イベントハンドラ
  onDataAvailable?: (blob: Blob) => void;
  onError?: (error: Error) => void;
}

/**
 * WebRTCベースの音声録音クラス（iOS Safari対応）
 */
export class WebRTCAudioRecorderImpl implements WebRTCAudioRecorder {
  stream: MediaStream | null = null;
  audioChunks: Blob[] = [];
  mimeType: string = 'audio/wav';
  isRecording: boolean = false;
  audioContext: AudioContext | null = null;
  mediaStreamSource: MediaStreamAudioSourceNode | null = null;
  processor: ScriptProcessorNode | null = null;
  recordedBuffers: Float32Array[] = [];
  sampleRate: number = 44100;
  
  onDataAvailable?: (blob: Blob) => void;
  onError?: (error: Error) => void;

  async initialize(preferredMimeTypes: string[], deviceId?: string): Promise<void> {
    try {
      console.log('WebRTC Audio Recorder: Initializing for iOS...', { deviceId: deviceId || 'default' });
      
      // 既存のリソースをクリーンアップ
      this.cleanup();
      
      // getUserMediaの制約オプションを構築
      const audioConstraints: MediaTrackConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 44100
      };
      
      // deviceIdが指定されている場合は追加
      if (deviceId) {
        audioConstraints.deviceId = { exact: deviceId };
      }
      
      // MediaStreamを取得
      this.stream = await navigator.mediaDevices.getUserMedia({ 
        audio: audioConstraints, 
        video: false 
      });
      
      console.log('WebRTC: MediaStream obtained');
      
      // AudioContextを作成（iOS Safari対応）
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      this.audioContext = new AudioContextClass();
      this.sampleRate = this.audioContext.sampleRate;
      
      console.log('WebRTC: AudioContext created, sample rate:', this.sampleRate);
      
      // MediaStreamSourceを作成
      this.mediaStreamSource = this.audioContext.createMediaStreamSource(this.stream);
      
      // ScriptProcessorNodeを作成（iOS Safari対応）
      // bufferSize: 4096は iOS Safari で安定動作する値
      this.processor = this.audioContext.createScriptProcessor(4096, 1, 1);
      
      // 録音データ処理
      this.processor.onaudioprocess = (event) => {
        if (!this.isRecording) return;
        
        const inputBuffer = event.inputBuffer;
        const inputData = inputBuffer.getChannelData(0);
        
        // Float32Arrayをコピーして保存
        const bufferCopy = new Float32Array(inputData.length);
        bufferCopy.set(inputData);
        this.recordedBuffers.push(bufferCopy);
      };
      
      // ノードを接続
      this.mediaStreamSource.connect(this.processor);
      this.processor.connect(this.audioContext.destination);
      
      // MIMEタイプを設定（WAVを使用）
      this.mimeType = 'audio/wav';
      
      console.log('WebRTC Audio Recorder: Initialization completed');
      
    } catch (error) {
      console.error('WebRTC Audio Recorder: Initialization failed:', error);
      this.cleanup();
      throw error;
    }
  }

  start(): void {
    if (!this.audioContext || !this.processor) {
      throw new Error('WebRTC Audio Recorder not initialized');
    }
    
    if (this.isRecording) {
      console.warn('WebRTC: Already recording');
      return;
    }
    
    console.log('WebRTC: Starting recording...');
    
    // AudioContextを再開（iOS Safari対応）
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
    
    // 録音バッファをクリア
    this.recordedBuffers = [];
    this.audioChunks = [];
    this.isRecording = true;
    
    console.log('WebRTC: Recording started');
  }

  async stop(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        if (!this.isRecording) {
          console.warn('WebRTC: Not recording');
          resolve(new Blob([], { type: this.mimeType }));
          return;
        }
        
        console.log('WebRTC: Stopping recording...');
        this.isRecording = false;
        
        if (this.recordedBuffers.length === 0) {
          console.warn('WebRTC: No audio data recorded');
          resolve(new Blob([], { type: this.mimeType }));
          return;
        }
        
        // 録音データをWAVファイルに変換
        const wavBlob = this.createWavBlob(this.recordedBuffers, this.sampleRate);
        
        console.log('WebRTC: Recording stopped, WAV blob created:', {
          size: wavBlob.size,
          type: wavBlob.type,
          buffers: this.recordedBuffers.length
        });
        
        // onDataAvailableコールバックを呼び出し
        if (this.onDataAvailable) {
          this.onDataAvailable(wavBlob);
        }
        
        resolve(wavBlob);
        
      } catch (error) {
        console.error('WebRTC: Error stopping recording:', error);
        if (this.onError) {
          this.onError(error instanceof Error ? error : new Error('Unknown error'));
        }
        reject(error);
      }
    });
  }

  cleanup(): void {
    console.log('WebRTC: Cleaning up...');
    
    this.isRecording = false;
    
    // ScriptProcessorNodeの切断
    if (this.processor) {
      try {
        this.processor.disconnect();
        this.processor.onaudioprocess = null;
      } catch (e) {
        console.warn('WebRTC: Error disconnecting processor:', e);
      }
      this.processor = null;
    }
    
    // MediaStreamSourceの切断
    if (this.mediaStreamSource) {
      try {
        this.mediaStreamSource.disconnect();
      } catch (e) {
        console.warn('WebRTC: Error disconnecting media stream source:', e);
      }
      this.mediaStreamSource = null;
    }
    
    // AudioContextのクローズ
    if (this.audioContext) {
      try {
        if (this.audioContext.state !== 'closed') {
          this.audioContext.close();
        }
      } catch (e) {
        console.warn('WebRTC: Error closing audio context:', e);
      }
      this.audioContext = null;
    }
    
    // MediaStreamトラックの停止
    if (this.stream) {
      this.stream.getTracks().forEach((track, index) => {
        try {
          if (track.readyState !== 'ended') {
            track.stop();
            console.log(`WebRTC: Track ${index} stopped: ${track.kind}`);
          }
        } catch (e) {
          console.error(`WebRTC: Error stopping track ${index}:`, e);
        }
      });
      this.stream = null;
    }
    
    // バッファをクリア
    this.recordedBuffers = [];
    this.audioChunks = [];
    
    console.log('WebRTC: Cleanup completed');
  }

  /**
   * Float32Array配列をWAVファイルのBlobに変換
   */
  private createWavBlob(buffers: Float32Array[], sampleRate: number): Blob {
    // 全バッファの長さを計算
    const totalLength = buffers.reduce((acc, buffer) => acc + buffer.length, 0);
    
    // 統合されたFloat32Arrayを作成
    const mergedBuffer = new Float32Array(totalLength);
    let offset = 0;
    for (const buffer of buffers) {
      mergedBuffer.set(buffer, offset);
      offset += buffer.length;
    }
    
    // Float32ArrayをInt16Arrayに変換（WAV形式用）
    const int16Buffer = new Int16Array(totalLength);
    for (let i = 0; i < totalLength; i++) {
      // -1.0 ～ 1.0 の範囲を -32768 ～ 32767 に変換
      const sample = Math.max(-1, Math.min(1, mergedBuffer[i]));
      int16Buffer[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
    }
    
    // WAVヘッダーを作成
    const wavHeader = this.createWavHeader(int16Buffer.length, sampleRate);
    
    // WAVファイルのBlobを作成
    const wavBlob = new Blob([wavHeader, int16Buffer], { type: 'audio/wav' });
    
    return wavBlob;
  }

  /**
   * WAVファイルのヘッダーを作成
   */
  private createWavHeader(dataLength: number, sampleRate: number): ArrayBuffer {
    const buffer = new ArrayBuffer(44);
    const view = new DataView(buffer);
    
    // WAVヘッダーの書き込み
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    const byteRate = sampleRate * 2; // 16bit mono
    const blockAlign = 2; // 16bit mono
    const dataSize = dataLength * 2; // 16bit
    
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true); // ファイルサイズ - 8
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true); // フォーマットチャンクサイズ
    view.setUint16(20, 1, true); // PCMフォーマット
    view.setUint16(22, 1, true); // モノラル
    view.setUint32(24, sampleRate, true); // サンプルレート
    view.setUint32(28, byteRate, true); // バイトレート
    view.setUint16(32, blockAlign, true); // ブロックアライン
    view.setUint16(34, 16, true); // ビット深度
    writeString(36, 'data');
    view.setUint32(40, dataSize, true); // データサイズ
    
    return buffer;
  }
}
